<script setup lang="ts">
// Реализация 360-просмотра без библиотеки circlr
// Логика основана на оригинальной библиотеке circlr, но переписана на Vue 3
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { IconClose, IconFullscreen, IconFullscreenExit } from '@/features/ui-kit/icons';

const emits = defineEmits(['setZIndex', 'close']);

interface Props {
  fullscreenEnable?: boolean;
  circularPhotos: string[];
  isCardViewOpenedProp?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fullscreenEnable: true,
  circularPhotos: () => [],
  isCardViewOpenedProp: false,
});

const isOverCircularPhoto = ref(false);
const isCardViewOpened = ref(false);
const circularText = 'Проведите пальцем для просмотра 360°';
const shadowTimer = ref<ReturnType<typeof setTimeout>>();
const isDirty = ref(false);

const circularView = ref<HTMLElement | null>(null);

// 360 rotation logic
const currentImageIndex = ref(0);
const touchStartX = ref<number | null>(null);
const touchStartIndex = ref(0);
const isDragging = ref(false);

onMounted(() => {
  initCircular();
});

onUnmounted(() => {
  unbindEvents();
});

const initCircular = () => {
  const el = circularView.value;
  if (!el) return;

  // Initialize: show first image, hide others
  const children = el.children;
  for (let i = 0; i < children.length; i++) {
    const img = children[i] as HTMLElement;
    img.style.opacity = i === 0 ? '1' : '0';
    img.style.position = 'absolute';
    img.style.top = '0';
    img.style.left = '0';
    img.style.width = '100%';
  }

  bindEvents();
};

const bindEvents = () => {
  const el = circularView.value;
  if (!el) return;

  el.addEventListener('mousedown', onTouchStart);
  el.addEventListener('mousemove', onTouchMove);
  document.addEventListener('mouseup', onTouchEnd);

  el.addEventListener('touchstart', onTouchStart);
  el.addEventListener('touchmove', onTouchMove);
  el.addEventListener('touchend', onTouchEnd);
};

const unbindEvents = () => {
  const el = circularView.value;
  if (!el) return;

  el.removeEventListener('mousedown', onTouchStart);
  el.removeEventListener('mousemove', onTouchMove);
  document.removeEventListener('mouseup', onTouchEnd);

  el.removeEventListener('touchstart', onTouchStart);
  el.removeEventListener('touchmove', onTouchMove);
  el.removeEventListener('touchend', onTouchEnd);
};

const getTouch = (event: MouseEvent | TouchEvent): number => {
  const el = circularView.value;
  if (!el) return 0;

  const isTouchEvent = 'touches' in event;
  const clientX = isTouchEvent
    ? (event as TouchEvent).touches[0]?.clientX || (event as TouchEvent).changedTouches[0]?.clientX
    : (event as MouseEvent).clientX;

  return clientX - el.offsetLeft;
};

const onTouchStart = (event: MouseEvent | TouchEvent) => {
  event.preventDefault();
  isDragging.value = true;
  touchStartX.value = getTouch(event);
  touchStartIndex.value = currentImageIndex.value;
};

const onTouchMove = (event: MouseEvent | TouchEvent) => {
  if (touchStartX.value === null || !isDragging.value) return;

  event.preventDefault();

  const el = circularView.value;
  if (!el) return;

  const touch = getTouch(event);
  const len = props.circularPhotos.length;
  const max = el.clientWidth;

  // Calculate offset: reverse direction
  let offset = -(touch - touchStartX.value);
  offset = Math.floor((offset / max) * len);

  // Calculate new index with cycling
  let newIndex = touchStartIndex.value + offset;

  // Cycle through images
  if (newIndex < 0) {
    newIndex = (newIndex % len) + len;
  }
  if (newIndex >= len) {
    newIndex = newIndex % len;
  }

  showImage(newIndex);
};

const onTouchEnd = (event: MouseEvent | TouchEvent) => {
  if (touchStartX.value === null) return;

  event.preventDefault();
  isDragging.value = false;
  touchStartX.value = null;
};

const showImage = (index: number) => {
  const el = circularView.value;
  if (!el) return;

  const children = el.children;
  const len = children.length;

  if (index < 0 || index >= len) return;

  // Hide current image
  if (children[currentImageIndex.value]) {
    (children[currentImageIndex.value] as HTMLElement).style.opacity = '0';
  }

  // Show new image
  if (children[index]) {
    (children[index] as HTMLElement).style.opacity = '1';
  }

  currentImageIndex.value = index;
};

const openModal = () => {
  isCardViewOpened.value = !isCardViewOpened.value;
  emits('setZIndex', isCardViewOpened.value);
  isOverCircularPhoto.value = false;
  nextTick(() => {
    initCircular();
  });
};

const close = () => {
  isCardViewOpened.value ? emits('close') : openModal();
};

const makeDirty = () => {
  isDirty.value = true;
  showControls();
};

const showControls = () => {
  if (isDirty.value) {
    isOverCircularPhoto.value = true;
    clearTimeout(shadowTimer.value);
    shadowTimer.value = setTimeout(() => hideControls(), 2000);
  }
};

const hideControls = () => {
  isOverCircularPhoto.value = false;
};

watch(
  () => isCardViewOpened.value,
  (value) => {
    if (!value) {
      document.querySelector('body')?.classList.add('overflow-hidden');
    } else {
      document.querySelector('body')?.classList.remove('overflow-hidden');
    }
  }
);

// Initialize with prop value
isCardViewOpened.value = props.isCardViewOpenedProp;
</script>

<template>
  <div
    class="interactive-content"
    id="interactive-content"
    :class="{ 'interactive-content--card-view': isCardViewOpened }"
    @click.self="close"
    @mouseover="isOverCircularPhoto = true"
  >
    <div class="circular-controls">
      <div class="close hover" @click="close" v-if="isOverCircularPhoto || !isCardViewOpened">
        <IconClose :size="23" color="inverse-primary" />
      </div>
    </div>

    <div class="circular-wrapper">
      <div
        class="circular-content"
        ref="circularView"
        :class="{ 'circular-content--fullscreen': !isCardViewOpened }"
      >
        <img
          class="circular-img"
          v-for="(photo, index) in circularPhotos"
          :key="index"
          :src="photo"
          @mouseout.self="showControls"
          @mousedown.self="makeDirty"
          @touchstart.self="makeDirty"
          draggable="false"
        />
      </div>

      <div
        class="circular-shadow"
        v-if="isOverCircularPhoto || !isDirty"
        :class="{ 'circular-shadow--fullscreen': !isCardViewOpened }"
      >
        <div class="circular-text">{{ circularText }}</div>
        <div v-if="fullscreenEnable" class="fullscreen" @click="openModal">
          <IconFullscreen v-if="isCardViewOpened" :size="13" color="inverse-primary" />
          <IconFullscreenExit v-else-if="!isCardViewOpened" :size="13" color="inverse-primary" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
#interactive-content
  position fixed
  top 0
  bottom 0
  right 0
  left 0
  background rgba(0, 0, 0, .8)
  z-index 6

  .circular-controls
    .close
      position absolute
      right 16px
      top 16px
      background rgba(85, 85, 85, .5)
      backdrop-filter blur(40px)
      border-radius 24px
      width 48px
      height 48px
      z-index 2
      display flex
      align-items center
      justify-content center

      &.close--card-view
        z-index 1

    .hover:hover
      background rgba(136, 136, 136, .5)
      cursor pointer

  .circular-wrapper
    display flex
    align-items center
    justify-content center
    width 100%
    height 100%

    &.circular-wrapper--card-view
      width 100%
      height 100%

    .circular-content
      width auto
      cursor ew-resize
      position relative

      &.circular-content--card-view
        width 100%
        height 100%
        object-fit cover

    .circular-content--fullscreen
      height 100%

      .circular-img
        height 100%
        position absolute
        top 0
        left 50%
        transform translateX(-50%)
        width auto !important
        user-select none
        -webkit-user-drag none

        @media (min-width 20em) and (max-width 56em)
          width 100vw !important

    .circular-shadow
      width 100%
      height 96px
      bottom 0
      left 0
      position absolute
      display flex
      align-items flex-end
      background linear-gradient(180deg, rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, .1) 25%, rgba(0, 0, 0, .4) 100%)

      .circular-text
        margin-left 16px
        margin-right 64px
        margin-bottom 16px
        text-align left
        font-size 13px
        color #ffffff

      .fullscreen
        position absolute
        bottom 16px
        border-radius 24px
        width 32px
        height 32px
        right 16px
        display flex
        align-items center
        justify-content center

        &:hover
          cursor pointer
          background rgba(85, 85, 85, .5)
          backdrop-filter blur(40px)

      &--fullscreen
        .circular-text
          margin-left auto
          margin-right auto
          padding 0 64px

        .fullscreen
          display none

#interactive-content.interactive-content--card-view
  width 100%
  height 100%
  position absolute
  top 0
  left 0
  background unset
  z-index 1

  .circular-controls
    .close
      z-index 1
      width 32px
      height 32px

  .circular-wrapper
    width 100%
    height 100%

    .circular-content
      width 100%
      height 100%
      object-fit cover

      img
        height 100%
        position absolute
        top 0
        bottom 0
        background-color #fff
        user-select none
        -webkit-user-drag none

    .circular-shadow
      width 100%
      height 96px
      bottom 0
      left 0
      position absolute

      &:hover
        background linear-gradient(180deg, rgba(0, 0, 0, 0) 0, rgba(0, 0, 0, .1) 25%, rgba(0, 0, 0, .4) 100%)

        .fullscreen
          position absolute
          bottom 16px
          border-radius 24px
          width 32px
          height 32px
          right 16px

          &:hover
            cursor pointer
            background rgba(85, 85, 85, .5)
            backdrop-filter blur(40px)
</style>
